import { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { LuSlidersHorizontal } from 'react-icons/lu';
import { FaChevronDown, FaRegHeart } from 'react-icons/fa';
import { RiShoppingCartLine } from 'react-icons/ri';
import { MdOutlinePlaylistAdd } from 'react-icons/md';
import Rating from '../components/Rating';
import Pagination from '../components/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import { price_range_product, query_products, get_category, get_category_filters } from '../store/reducers/homeReducer';
import { add_to_card, add_to_wishlist, messageClear } from '../store/reducers/cardReducer';
import { add_to_my_list, messageClear as myListMessageClear } from '../store/reducers/myListReducer';
import toast from 'react-hot-toast';
import { useCurrency } from '../context/CurrencyContext';
import PageSeo from '../components/seo/PageSeo';
import { useCategorySeo } from '../hooks/useSeo';

const CategoryShop = () => {

    let [searchParams] = useSearchParams();
    const category = searchParams.get('category');
    const subCategory = searchParams.get('subCategory');
    const categorySeo = useCategorySeo(category, subCategory);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { products, categorys, priceRange, totalProduct, parPage, categoryFilters } = useSelector(state => state.home);
    const { userInfo } = useSelector(state => state.auth);
    const { errorMessage, successMessage } = useSelector(state => state.card);
    const { successMessage: myListSuccessMessage, errorMessage: myListErrorMessage } = useSelector(state => state.myList);
    const { formatBase } = useCurrency();

    const [pageNumber, setPageNumber] = useState(1);
    const [sortPrice, setSortPrice] = useState('');
    const [selectedFilters, setSelectedFilters] = useState({});
    const filterScrollRef = useRef(null);
    const categoryRailRef = useRef(null);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    const decodedCategory = decodeURIComponent(category || '').toLowerCase();
    const decodedSubCategory = decodeURIComponent(subCategory || '').toLowerCase();
    const toSlug = (str = '') =>
        str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const toTitle = (str = '') =>
        decodeURIComponent(str || '')
            .replace(/-/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/\b\w/g, (char) => char.toUpperCase());

    const activeCategory = categorys?.find(cat => cat.name.toLowerCase() === decodedCategory);
    const isClothingCategory = /clothing|fashion|apparel/i.test(activeCategory?.name || decodedCategory);
    const activeSubCategory = activeCategory?.subcategories?.find(sub =>
        sub.name.toLowerCase() === decodedSubCategory || toSlug(sub.name) === decodedSubCategory
    );
    const categoryTitle = activeSubCategory?.name || activeCategory?.name || toTitle(category) || 'All Categories';
    const railItems = activeCategory?.subcategories?.length ? activeCategory.subcategories : categorys;
    const selectedFilterCount = Object.values(selectedFilters).reduce((sum, values) => sum + values.length, 0);

    const toggleFilterValue = (type, value) => {
        setSelectedFilters((prev) => {
            const next = { ...prev };
            const existing = new Set(next[type] || []);
            if (existing.has(value)) {
                existing.delete(value);
            } else {
                existing.add(value);
            }
            if (existing.size === 0) {
                delete next[type];
            } else {
                next[type] = Array.from(existing).sort((a, b) => a.localeCompare(b));
            }
            return next;
        });
        setPageNumber(1);
    };

    const resetFilters = () => {
        setSelectedFilters({});
        setPageNumber(1);
    };

    useEffect(() => {
        dispatch(price_range_product());
        dispatch(get_category());
    }, [dispatch]);

    useEffect(() => {
        dispatch(get_category_filters({ category: category || '', subCategory: subCategory || '' }));
    }, [category, subCategory, dispatch]);

    useEffect(() => {
        dispatch(
            query_products({
                low: priceRange.low || '',
                high: priceRange.high || '',
                category,
                subCategory,
                rating: '',
                sortPrice,
                pageNumber,
                filters: selectedFilters
            })
        );
    }, [category, subCategory, pageNumber, priceRange.low, priceRange.high, sortPrice, selectedFilters, dispatch]);

    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage);
            dispatch(messageClear());
        }
        if (errorMessage) {
            toast.error(errorMessage);
            dispatch(messageClear());
        }
    }, [successMessage, errorMessage, dispatch]);

    useEffect(() => {
        if (myListSuccessMessage) {
            toast.success(myListSuccessMessage);
            dispatch(myListMessageClear());
        }
        if (myListErrorMessage) {
            toast.error(myListErrorMessage);
            dispatch(myListMessageClear());
        }
    }, [myListSuccessMessage, myListErrorMessage, dispatch]);

    const scrollRail = (ref, distance) => {
        ref.current?.scrollBy({ left: distance, behavior: 'smooth' });
    };

    const add_card = (id) => {
        if (userInfo) {
            dispatch(add_to_card({ userId: userInfo.id, quantity: 1, productId: id }));
        } else {
            navigate('/login');
        }
    };

    const add_wishlist = (pro) => {
        if (!userInfo) { navigate('/login'); return; }
        dispatch(add_to_wishlist({
            userId: userInfo.id,
            productId: pro._id,
            name: pro.name,
            price: pro.price,
            image: pro.images[0],
            discount: pro.discount,
            rating: pro.rating,
            slug: pro.slug
        }));
    };

    const add_my_list = (pro) => {
        if (!userInfo) { navigate('/login'); return; }
        dispatch(add_to_my_list({ userId: userInfo.id, productId: pro._id }));
    };

    const calculateFinalPrice = (price, discount) =>
        discount ? (price - (price * discount) / 100) : price;

    return (
        <div className="min-h-screen bg-[#f7f7f7]">
            {categorySeo && <PageSeo {...categorySeo} />}
            <Header />

            <main className="mx-auto max-w-[1420px] px-6 pb-12 xl:px-4 sm:px-3">
                <section className="mt-4 rounded-[28px] bg-white px-5 py-4 shadow-[0_4px_16px_rgba(0,0,0,0.06)] sm:rounded-[20px] sm:px-3">

                    {/* Breadcrumb + title */}
                    <div className="mb-4 border-b border-gray-100 pb-4">
                        <nav className="flex items-center gap-1.5 text-[12px] text-gray-500">
                            <Link to="/" className="hover:text-[#E21C1B] transition">Home</Link>
                            <IoIosArrowForward size={10} className="text-gray-300 shrink-0" />
                            {activeCategory ? (
                                <Link
                                    to={`/products?category=${encodeURIComponent(activeCategory.name)}`}
                                    className="hover:text-[#E21C1B] transition"
                                >
                                    {activeCategory.name}
                                </Link>
                            ) : (
                                <span>Products</span>
                            )}
                            {activeSubCategory && (
                                <>
                                    <IoIosArrowForward size={10} className="text-gray-300 shrink-0" />
                                    <span className="font-medium text-gray-800">{activeSubCategory.name}</span>
                                </>
                            )}
                        </nav>
                        <div className="mt-1.5 flex items-baseline gap-2">
                            <h1 className="text-[20px] font-bold leading-tight text-gray-900 md:text-[18px]">
                                {categoryTitle}
                            </h1>
                            <span className="text-[13px] text-gray-400">
                                {totalProduct || products.length} items
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => scrollRail(categoryRailRef, -360)}
                            aria-label="Scroll categories left"
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:border-[#E21C1B] hover:text-[#E21C1B]"
                        >
                            <IoIosArrowBack size={18} />
                        </button>

                        <div ref={categoryRailRef} className="flex flex-1 gap-4 overflow-x-auto scroll-smooth py-1 scrollbar-hide">
                            {railItems?.map((item, index) => {
                                const isSubItem = Boolean(activeCategory?.subcategories?.length);
                                const itemName = item.name;
                                const isActive = isSubItem
                                    ? (activeSubCategory?.name === itemName || (!activeSubCategory && index === 0))
                                    : activeCategory?.name === itemName;
                                const href = isSubItem
                                    ? `/products?category=${encodeURIComponent(activeCategory.name)}&subCategory=${encodeURIComponent(toSlug(itemName))}`
                                    : `/products?category=${encodeURIComponent(itemName)}`;

                                return (
                                    <Link
                                        key={`${itemName}-${index}`}
                                        to={href}
                                        className={`group flex w-[120px] shrink-0 flex-col items-center gap-2 rounded-[22px] px-2 py-2 text-center transition ${isActive ? 'bg-[#fff2eb] text-[#E21C1B]' : 'text-gray-700 hover:bg-gray-50 hover:text-[#E21C1B]'}`}
                                    >
                                        <div className={`h-[86px] w-[86px] overflow-hidden rounded-full border-2 bg-gray-100 transition group-hover:scale-[1.03] ${isActive ? 'border-[#E21C1B]' : 'border-gray-100 group-hover:border-[#E21C1B]'}`}>
                                            {item.image ? (
                                                <img src={item.image} alt={itemName} className="h-full w-full object-cover" />
                                            ) : (
                                                <span className="flex h-full w-full items-center justify-center text-2xl font-black uppercase text-gray-300">
                                                    {itemName?.[0]}
                                                </span>
                                            )}
                                        </div>
                                        <span className="line-clamp-2 min-h-[32px] text-[12px] font-semibold leading-tight">
                                            {itemName}
                                        </span>
                                    </Link>
                                );
                            })}
                        </div>

                        <button
                            onClick={() => scrollRail(categoryRailRef, 360)}
                            aria-label="Scroll categories right"
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:border-[#E21C1B] hover:text-[#E21C1B]"
                        >
                            <IoIosArrowForward size={18} />
                        </button>
                    </div>
                </section>

                <div className="sticky top-0 z-20 mt-5 rounded-full bg-white/95 shadow-[0_8px_24px_rgba(0,0,0,0.06)] backdrop-blur">
                    <div className="flex items-center gap-2 px-3 py-3 sm:flex-wrap">
                        <button
                            onClick={() => setFilterDrawerOpen(true)}
                            className="inline-flex h-9 shrink-0 items-center gap-2 rounded-full bg-gray-100 px-4 text-[13px] font-semibold text-gray-800 transition hover:bg-gray-200"
                        >
                            <LuSlidersHorizontal size={15} />
                            Filters
                            {selectedFilterCount > 0 && (
                                <span className="rounded-full bg-[#E21C1B] px-1.5 py-0.5 text-[10px] text-white">{selectedFilterCount}</span>
                            )}
                        </button>

                        <div className="relative shrink-0">
                            <select
                                value={sortPrice}
                                onChange={e => { setSortPrice(e.target.value); setPageNumber(1); }}
                                className="h-9 appearance-none rounded-full bg-gray-100 py-0 pl-4 pr-9 text-[13px] font-semibold text-gray-800 outline-none transition hover:bg-gray-200"
                            >
                                <option value="">Sort by: Relevance</option>
                                <option value="low-to-high">Price: Low to High</option>
                                <option value="high-to-low">Price: High to Low</option>
                            </select>
                            <FaChevronDown size={10} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-500" />
                        </div>

                        <div className="relative min-w-0 flex-1 overflow-hidden">
                            <div ref={filterScrollRef} className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
                                {categoryFilters.map((filter) => {
                                    const selectedCount = selectedFilters[filter.type]?.length || 0;
                                    return (
                                        <button
                                            key={filter.type}
                                            onClick={() => setFilterDrawerOpen(true)}
                                            className={`inline-flex h-9 items-center gap-1.5 rounded-full px-4 text-[12px] font-semibold whitespace-nowrap transition ${selectedCount ? 'bg-[#fff2eb] text-[#E21C1B] ring-1 ring-[#E21C1B]/30' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                        >
                                            {filter.type}{selectedCount ? ` (${selectedCount})` : ''}
                                            <FaChevronDown size={9} />
                                        </button>
                                    );
                                })}

                                {priceRange.low !== undefined && (
                                    <button
                                        onClick={() => setFilterDrawerOpen(true)}
                                        className="inline-flex h-9 items-center gap-1.5 rounded-full bg-gray-100 px-4 text-[12px] font-semibold text-gray-700 whitespace-nowrap transition hover:bg-gray-200"
                                    >
                                        Price <FaChevronDown size={9} />
                                    </button>
                                )}

                                <button className="inline-flex h-9 items-center rounded-full bg-gray-100 px-4 text-[12px] font-semibold text-gray-700 whitespace-nowrap transition hover:bg-gray-200">
                                    On Sale
                                </button>

                                <button className="inline-flex h-9 items-center rounded-full bg-gray-100 px-4 text-[12px] font-semibold text-gray-700 whitespace-nowrap transition hover:bg-gray-200">
                                    In Stock
                                </button>
                            </div>

                            <button
                                onClick={() => scrollRail(filterScrollRef, 220)}
                                aria-label="Scroll filters right"
                                className="absolute right-0 top-0 flex h-full w-12 items-center justify-end bg-gradient-to-l from-white via-white to-transparent text-gray-500"
                            >
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow">
                                    <IoIosArrowForward size={16} />
                                </span>
                            </button>
                        </div>

                        <span className="shrink-0 rounded-full bg-[#1f1f1f] px-3 py-2 text-[12px] font-bold text-white sm:hidden">
                            {totalProduct} items
                        </span>
                    </div>
                </div>

                {filterDrawerOpen && (
                    <div className="fixed inset-0 z-50">
                        <div
                            className="absolute inset-0 bg-black/35"
                            onClick={() => setFilterDrawerOpen(false)}
                        />
                        <div className="absolute right-0 top-0 flex h-full w-full max-w-[430px] flex-col bg-white shadow-2xl">
                            <div className="flex items-center justify-between border-b px-6 py-5">
                                <div>
                                    <h2 className="text-2xl font-black text-gray-900">Filters</h2>
                                    <p className="text-sm text-gray-500">{totalProduct} matching products</p>
                                </div>
                                <button
                                    onClick={() => setFilterDrawerOpen(false)}
                                    className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-2xl leading-none text-gray-500 transition hover:bg-gray-200 hover:text-gray-900"
                                    aria-label="Close filters"
                                >
                                    x
                                </button>
                            </div>

                            <div className="flex-1 space-y-8 overflow-y-auto px-6 py-5">
                                {categoryFilters.length === 0 && (
                                    <p className="text-sm text-gray-500">No filters available for this category.</p>
                                )}

                                {categoryFilters.map((filter) => (
                                    <div key={filter.type} className="space-y-4">
                                        <h3 className="text-lg font-bold text-gray-800">{filter.type}</h3>
                                        <div className="grid grid-cols-2 gap-3">
                                            {filter.values.map((value) => {
                                                const checked = (selectedFilters[filter.type] || []).includes(value);
                                                return (
                                                    <label
                                                        key={`${filter.type}-${value}`}
                                                        className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-2 text-sm transition ${checked ? 'border-[#E21C1B] bg-[#fff2eb] text-[#E21C1B]' : 'border-gray-200 text-gray-700 hover:border-[#E21C1B] hover:text-[#E21C1B]'}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => toggleFilterValue(filter.type, value)}
                                                            className="accent-[#E21C1B]"
                                                        />
                                                        <span className="truncate">{value}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="flex items-center gap-3 border-t px-6 py-4">
                                <button
                                    onClick={resetFilters}
                                    className="flex-1 rounded-full border border-gray-300 py-2 text-sm font-bold text-gray-800 transition hover:border-[#E21C1B] hover:text-[#E21C1B]"
                                >
                                    Reset
                                </button>
                                <button
                                    onClick={() => setFilterDrawerOpen(false)}
                                    className="flex-[2] rounded-full bg-[#E21C1B] py-2 text-sm font-bold text-white transition hover:bg-[#c91413]"
                                >
                                    Show results
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <section className="mt-5">
                    <div className="grid grid-cols-2 gap-x-3 gap-y-4 mds:grid-cols-3 lgs:grid-cols-4 xls:grid-cols-5 sm:gap-x-2 sm:gap-y-3">
                        {products.map((p, i) => {
                            const finalPrice = calculateFinalPrice(p.price, p.discount);
                            const isLowStock = Number(p.stock) > 0 && Number(p.stock) <= 5;
                            return (
                                <div
                                    key={p._id || i}
                                    className="group min-w-0 bg-white transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(0,0,0,0.12)]"
                                >
                                    <div className="relative overflow-hidden bg-gray-100">
                                        <Link to={`/product/details/${p.slug}`} target="_blank" rel="noopener noreferrer" className="block">
                                            <div className={`w-full overflow-hidden ${isClothingCategory ? 'aspect-[2/3]' : 'aspect-square'}`}>
                                                <img
                                                    src={p.images[0]}
                                                    alt={p.name}
                                                    className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]"
                                                />
                                            </div>
                                        </Link>

                                        <div className="absolute left-2 top-2 flex flex-wrap gap-1.5">
                                            {p.freeShipping && (
                                                <span className="rounded bg-[#0a8f08] px-1.5 py-0.5 text-[10px] font-black leading-none text-white">
                                                    Local
                                                </span>
                                            )}
                                            {isLowStock && (
                                                <span className="rounded bg-orange-500 px-1.5 py-0.5 text-[10px] font-black leading-none text-white">
                                                    Only {p.stock} left
                                                </span>
                                            )}
                                        </div>

                                        <button
                                            onClick={() => add_wishlist(p)}
                                            aria-label="Add to wishlist"
                                            className="absolute right-2 top-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/90 text-gray-500 shadow transition hover:text-[#E21C1B]"
                                        >
                                            <FaRegHeart size={13} />
                                        </button>

                                        <button
                                            onClick={() => add_my_list(p)}
                                            aria-label="Add to My List"
                                            title="Add to My List"
                                            className="absolute right-2 top-11 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/90 text-gray-500 shadow transition hover:text-[#1769e0]"
                                        >
                                            <MdOutlinePlaylistAdd size={16} />
                                        </button>

                                        <button
                                            onClick={() => add_card(p._id)}
                                            aria-label="Add to cart"
                                            className="absolute bottom-2 right-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1f1f1f]/90 text-white shadow transition hover:bg-[#E21C1B]"
                                        >
                                            <RiShoppingCartLine size={16} />
                                        </button>
                                    </div>

                                    <div className="px-1.5 pb-2 pt-2">
                                        <Link to={`/product/details/${p.slug}`} target="_blank" rel="noopener noreferrer">
                                            <h2 className="line-clamp-2 min-h-[34px] text-[13px] leading-[17px] text-gray-800 transition group-hover:text-[#E21C1B]">
                                                {p.name}
                                            </h2>
                                        </Link>

                                        <div className="mt-1 flex flex-wrap items-baseline gap-1">
                                            {p.discount > 0 && (
                                                <span className="text-[12px] font-semibold text-gray-400 line-through">
                                                    {formatBase(p.price)}
                                                </span>
                                            )}
                                            <span className="text-[16px] font-black leading-tight text-[#1f1f1f]">
                                                {formatBase(finalPrice)}
                                            </span>
                                        </div>

                                        <div className="mt-1 flex items-center gap-1 text-[11px] text-gray-500">
                                            <span className="flex text-[11px]">
                                                <Rating ratings={p.rating} />
                                            </span>
                                            <span>{p.rating || 0}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {totalProduct > parPage && (
                    <div className="mt-8">
                        <Pagination
                            pageNumber={pageNumber}
                            setPageNumber={setPageNumber}
                            totalItem={totalProduct}
                            parPage={parPage}
                            showItem={Math.floor(totalProduct / parPage)}
                        />
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
};

export default CategoryShop;
